/**
 * NotificationSettingsPage Component
 * Full-page notification settings with subscriptions, channels, and preferences
 */

import React, { useState } from 'react';
import {
  Card,
  Switch,
  Select,
  Button,
  Divider,
  Space,
  Spin,
  message,
  TimePicker,
  Collapse,
} from 'antd';
import {
  useSubscriptions,
  useChannelPreferences,
  useUserOptions,
  useUpdateSubscription,
  useUpdateChannelPreference,
  useUpdateUserOptions,
} from '@/hooks/useNotifications';
import { ModernLayout } from '@/components/layout/ModernLayout';
import { PushNotificationSetup } from '@/components/features/notifications/PushNotificationSetup';
import type {
  Subscription,
  ChannelPreference,
  UserOption,
  UpdateSubscriptionRequest,
  UpdateChannelPreferenceRequest,
  UpdateUserOptionsRequest,
} from '@/api/types/notification.types';
import dayjs from 'dayjs';
import './NotificationSettingsPage.css';

const { Option } = Select;
const { Panel } = Collapse;

export const NotificationSettingsPage: React.FC = () => {
  const { data: subscriptions, isLoading: subscriptionsLoading } =
    useSubscriptions();
  const { data: channels, isLoading: channelsLoading } =
    useChannelPreferences();
  const { data: options, isLoading: optionsLoading } = useUserOptions();

  const { mutate: updateSubscription, isPending: subscriptionPending } =
    useUpdateSubscription();
  const { mutate: updateChannel, isPending: channelPending } =
    useUpdateChannelPreference();
  const { mutate: updateOptions, isPending: optionsPending } =
    useUpdateUserOptions();

  const [localOptions, setLocalOptions] = useState<Partial<UserOption>>({});

  React.useEffect(() => {
    if (options) {
      setLocalOptions({
        sound_enabled: options.sound_enabled,
        digest_frequency: options.digest_frequency,
        quiet_hours_start: options.quiet_hours_start,
        quiet_hours_end: options.quiet_hours_end,
      });
    }
  }, [options]);

  const handleSubscriptionToggle = (
    subscription: Subscription,
    checked: boolean
  ) => {
    const data: UpdateSubscriptionRequest = {
      type: subscription.type,
      is_active: checked,
    };
    updateSubscription(
      { subscriptionId: subscription.id, data },
      {
        onSuccess: () => {
          message.success('Subscription updated');
        },
        onError: () => {
          message.error('Failed to update subscription');
        },
      }
    );
  };

  const handleChannelToggle = (
    channel: ChannelPreference,
    checked: boolean
  ) => {
    const data: UpdateChannelPreferenceRequest = {
      channel: channel.channel,
      is_active: checked,
    };
    updateChannel(
      { preferenceId: channel.id, data },
      {
        onSuccess: () => {
          message.success('Channel preference updated');
        },
        onError: () => {
          message.error('Failed to update channel preference');
        },
      }
    );
  };

  const handleOptionsUpdate = () => {
    const data: UpdateUserOptionsRequest = {
      sound_enabled: localOptions.sound_enabled,
      digest_frequency: localOptions.digest_frequency,
      quiet_hours_start: localOptions.quiet_hours_start,
      quiet_hours_end: localOptions.quiet_hours_end,
    };
    updateOptions(data, {
      onSuccess: () => {
        message.success('Options updated');
      },
      onError: () => {
        message.error('Failed to update options');
      },
    });
  };

  const getSubscriptionLabel = (type: string) => {
    const labels: Record<string, string> = {
      'Notifications.subscription_type.TICKET_ASSIGNED': 'Ticket Assigned',
      'Notifications.subscription_type.TICKET_COMMENT': 'Ticket Comments',
      'Notifications.subscription_type.TICKET_STATUS_CHANGED':
        'Ticket Status Changed',
      'Notifications.subscription_type.LEAD_ASSIGNED': 'Lead Assigned',
      'Notifications.subscription_type.LEAD_STATUS_CHANGED':
        'Lead Status Changed',
      'Notifications.subscription_type.CHAT_MESSAGE': 'Chat Messages',
      'Notifications.subscription_type.MENTION': 'Mentions',
      'Notifications.subscription_type.SYSTEM_ANNOUNCEMENT':
        'System Announcements',
      'Notifications.subscription_type.CALENDAR_REMINDER': 'Calendar Reminders',
    };
    return labels[type] || type;
  };

  const getChannelLabel = (channel: string) => {
    const labels: Record<string, string> = {
      'Notifications.delivery_channel.WEBSOCKET': 'WebSocket (Real-time)',
      'Notifications.delivery_channel.EMAIL': 'Email',
      'Notifications.delivery_channel.SMS': 'SMS',
      'Notifications.delivery_channel.PUSH': 'Web Push',
    };
    return labels[channel] || channel;
  };

  const getDigestLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      'Notifications.digest_frequency.DISABLED': 'Disabled',
      'Notifications.digest_frequency.DAILY': 'Daily',
      'Notifications.digest_frequency.WEEKLY': 'Weekly',
    };
    return labels[frequency] || frequency;
  };

  if (subscriptionsLoading || channelsLoading || optionsLoading) {
    return (
      <ModernLayout>
        <div className="notification-settings-loading">
          <Spin size="large" />
        </div>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout>
      <div className="notification-settings-page">
        {/* Header */}
        <div className="notification-settings-header">
          <h1>Notification Settings</h1>
          <p>Manage your notification preferences and delivery channels</p>
        </div>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Notification Types */}
          <Card
            title="Notification Types"
            className="notification-settings-card"
          >
            <p className="notification-settings-description">
              Choose which types of notifications you want to receive
            </p>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {subscriptions?.map((subscription) => (
                <div
                  key={subscription.id}
                  className="notification-settings-item"
                >
                  <div className="notification-settings-item-label">
                    {getSubscriptionLabel(subscription.type)}
                  </div>
                  <Switch
                    checked={subscription.is_active}
                    onChange={(checked) =>
                      handleSubscriptionToggle(subscription, checked)
                    }
                    loading={subscriptionPending}
                  />
                </div>
              ))}
            </Space>
          </Card>

          {/* Delivery Channels */}
          <Card
            title="Delivery Channels"
            className="notification-settings-card"
          >
            <p className="notification-settings-description">
              Choose how you want to receive notifications
            </p>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {channels?.map((channel) => (
                <div key={channel.id} className="notification-settings-item">
                  <div className="notification-settings-item-label">
                    {getChannelLabel(channel.channel)}
                  </div>
                  <Switch
                    checked={channel.is_active}
                    onChange={(checked) =>
                      handleChannelToggle(channel, checked)
                    }
                    loading={channelPending}
                  />
                </div>
              ))}
            </Space>
          </Card>

          {/* Web Push Notifications */}
          <Card
            title="Browser Push Notifications"
            className="notification-settings-card"
          >
            <p className="notification-settings-description">
              Receive notifications even when you&apos;re not on the site
            </p>
            <PushNotificationSetup />
          </Card>

          {/* User Options */}
          <Card title="Preferences" className="notification-settings-card">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Sound */}
              <div className="notification-settings-item">
                <div className="notification-settings-item-label">
                  <div>Sound Notifications</div>
                  <div className="notification-settings-item-description">
                    Play sound when receiving notifications
                  </div>
                </div>
                <Switch
                  checked={localOptions.sound_enabled}
                  onChange={(checked) =>
                    setLocalOptions({ ...localOptions, sound_enabled: checked })
                  }
                />
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* Digest Frequency */}
              <div className="notification-settings-item">
                <div className="notification-settings-item-label">
                  <div>Email Digest</div>
                  <div className="notification-settings-item-description">
                    Receive bundled notifications via email
                  </div>
                </div>
                <Select
                  value={localOptions.digest_frequency}
                  onChange={(value) =>
                    setLocalOptions({
                      ...localOptions,
                      digest_frequency: value,
                    })
                  }
                  style={{ width: 200 }}
                >
                  <Option value="Notifications.digest_frequency.DISABLED">
                    {getDigestLabel('Notifications.digest_frequency.DISABLED')}
                  </Option>
                  <Option value="Notifications.digest_frequency.DAILY">
                    {getDigestLabel('Notifications.digest_frequency.DAILY')}
                  </Option>
                  <Option value="Notifications.digest_frequency.WEEKLY">
                    {getDigestLabel('Notifications.digest_frequency.WEEKLY')}
                  </Option>
                </Select>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* Quiet Hours */}
              <Collapse ghost>
                <Panel header="Quiet Hours" key="quiet-hours">
                  <div className="notification-settings-item">
                    <div className="notification-settings-item-label">
                      <div>Start Time</div>
                      <div className="notification-settings-item-description">
                        Don&apos;t send notifications after this time
                      </div>
                    </div>
                    <TimePicker
                      format="HH:mm"
                      value={
                        localOptions.quiet_hours_start
                          ? dayjs(localOptions.quiet_hours_start, 'HH:mm:ss')
                          : null
                      }
                      onChange={(time) =>
                        setLocalOptions({
                          ...localOptions,
                          quiet_hours_start: time?.format('HH:mm:ss') || null,
                        })
                      }
                    />
                  </div>

                  <div className="notification-settings-item">
                    <div className="notification-settings-item-label">
                      <div>End Time</div>
                      <div className="notification-settings-item-description">
                        Resume sending notifications after this time
                      </div>
                    </div>
                    <TimePicker
                      format="HH:mm"
                      value={
                        localOptions.quiet_hours_end
                          ? dayjs(localOptions.quiet_hours_end, 'HH:mm:ss')
                          : null
                      }
                      onChange={(time) =>
                        setLocalOptions({
                          ...localOptions,
                          quiet_hours_end: time?.format('HH:mm:ss') || null,
                        })
                      }
                    />
                  </div>
                </Panel>
              </Collapse>
            </Space>

            <Divider />

            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                onClick={handleOptionsUpdate}
                loading={optionsPending}
              >
                Save Preferences
              </Button>
            </div>
          </Card>
        </Space>
      </div>
    </ModernLayout>
  );
};
